import React, { useEffect, useState } from 'react';
import { RotateCcw, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';

const RecycleBin = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [restoringId, setRestoringId] = useState(null);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${getApiUrl()}/api/recycle-bin`, { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) setBatches(result.data);
      else setError(result.message || 'Failed to load recycle bin');
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const toggleDetails = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (details[id]) return;
    try {
      const response = await fetch(`${getApiUrl()}/api/recycle-bin/${id}`, { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) {
        setDetails((prev) => ({ ...prev, [id]: result.data }));
      }
    } catch (err) {
      setError('Failed to load batch details');
    }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    setError('');
    try {
      const response = await fetch(`${getApiUrl()}/api/recycle-bin/${id}/restore`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (result.success) {
        fetchBatches();
      } else {
        setError(result.message || 'Failed to restore');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="neo-raised overflow-hidden">
        {batches.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-dark-muted">
            <Trash2 className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
            <p className="font-medium">Recycle bin is empty</p>
            <p className="text-sm mt-1">Deleted records and their linked data will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200/70 dark:divide-dark-border">
            {batches.map((batch) => (
              <div key={batch.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => toggleDetails(batch.id)}
                    className="flex items-start gap-2 text-left min-w-0"
                  >
                    {expandedId === batch.id ? (
                      <ChevronDown className="w-4 h-4 mt-1 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mt-1 text-gray-400" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-dark-text">{batch.label}</p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                        {batch.itemCount} linked item{batch.itemCount === 1 ? '' : 's'} • {new Date(batch.deletedAt).toLocaleString()}
                        {batch.restoredAt ? ` • Restored ${new Date(batch.restoredAt).toLocaleString()}` : ''}
                      </p>
                    </div>
                  </button>
                  {!batch.restoredAt && (
                    <button
                      type="button"
                      onClick={() => handleRestore(batch.id)}
                      disabled={restoringId === batch.id}
                      className="neo-btn-primary px-3 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      <RotateCcw className="w-4 h-4" />
                      {restoringId === batch.id ? 'Restoring...' : 'Restore'}
                    </button>
                  )}
                </div>
                {expandedId === batch.id && details[batch.id] && (
                  <ul className="mt-3 ml-6 space-y-1">
                    {details[batch.id].items.map((item) => (
                      <li key={item.id} className="text-sm text-gray-700 dark:text-dark-muted">
                        <span className="uppercase text-xs text-gray-400 mr-2">{item.action}</span>
                        {item.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecycleBin;
